import * as Sentry from "@sentry/react";
import axios from "axios";

import { invalidateSession } from "../ui library/helpers/functions";

import { getToken, isAuthenticated, refreshTokenCookie } from "./auth";

const allowedSources = ["healthnsyncinfra.net", "healthnsync.com", "netproapp.com"];

const isTrustedSource = (url) => {
  if (!url) {
    return false;
  }
  try {
    const hostname = new URL(url).hostname;
    return allowedSources.some((domain) => hostname.endsWith(domain));
  } catch {
    return false;
  }
};

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "https://api.healthnsync.com",
  withCredentials: true,
});

apiClient.interceptors.request.use((request) => {
  try {
    const isLoginUrl = request.url.includes("/auth/login");
    const isPasswordReset =
      request.url.includes("/api/password_reset/") && request.method === "post";
    const isPasswordUpdate =
      request.url.includes("/set_password") && request.method === "patch";

    // Skip auth checks for endpoints that are intentionally available before login.
    if (isLoginUrl || isPasswordReset || isPasswordUpdate) {
      return request;
    }

    const token = getToken();

    // Block the request if the cookie is missing or expired
    if (!isAuthenticated()) {
      invalidateSession();
      // Return a rejected promise so the request never goes out
      return Promise.reject(new Error("No valid session cookie. Request blocked."));
    }

    // Inject the Authorization header if not already present (e.g. admin-panel auth_token flow)
    const hasAuthHeader =
      Boolean(request.headers?.Authorization) || Boolean(request.headers?.authorization);

    if (!hasAuthHeader) {
      request.headers.Authorization = `Token ${token}`;
    }

    return request;
  } catch (error) {
    console.error(error);
    invalidateSession();
    return Promise.reject(error);
  }
});

apiClient.interceptors.response.use(
  (response) => {
    // Slide the 1-hour cookie window on every successful response
    refreshTokenCookie();

    // Keep localStorage token_exp in sync for the session-warning system
    const newExpiration = response?.headers?.["x-token-expiry"];
    if (newExpiration) {
      const { shouldUpdateTokenExpiration } = require("../ui library/helpers/sessionFunctions");
      if (shouldUpdateTokenExpiration(newExpiration)) {
        localStorage.setItem("token_exp", newExpiration);
      }
    }

    return response;
  },
  async (error) => {
    const { response, config } = error;

    const trusted = isTrustedSource(config?.url);

    if (!response) {
      if (trusted) {
        Sentry.captureException(error, {
          tags: { type: "network" },
          extra: {
            message: error.message,
            config: config,
          },
        });
      }
      return Promise.reject(error);
    }

    if (response.status === 401 && trusted) {
      console.warn("401 Unauthorized:", config?.method?.toUpperCase(), config?.url);
      Sentry.captureMessage("401 Unauthorized", {
        level: "info",
        extra: {
          url: config?.url,
          method: config?.method,
        },
      });
      await invalidateSession();
    } else if (response.status === 403 && trusted) {
      Sentry.captureException(error, {
        tags: { type: "403" },
        extra: {
          url: config?.url,
          method: config?.method,
          response: response?.data,
        },
      });
    }

    return Promise.reject(error);
  }
);

export default apiClient;
